// ---------------------------------------------------------------------------
// Seasonal routing (Morgan: "we need to be seasonal about this… spring/summer →
// weight the Seine / waterfront / greenery higher").
//
// A thin MODULATION layer over the existing pleasantness scoring — not new
// routing. Two pieces:
//   1. Nature-weight multipliers per season (water + greenery), applied inside
//      scenicScore so the SAME A→B leans toward the waterfront in June and the
//      foliage backroads in October.
//   2. A small seasonal-closure list so we never route someone onto a scenic
//      road that's snow-gated today (Going-to-the-Sun, Tioga Pass, …).
//
// Hemisphere-aware: a December trip in Sydney is summer. Date comes from the
// device clock; latitude from the trip's midpoint. All multipliers default to
// 1.0 (no change) when no season is supplied, so callers are opt-in and every
// existing score is unaffected.
// ---------------------------------------------------------------------------

export type Season = "spring" | "summer" | "fall" | "winter";

/** Meteorological season for a date at a given latitude, hemisphere-aware. */
export function seasonFor(date: Date, lat: number): Season {
  const m = date.getMonth(); // 0 = Jan … 11 = Dec
  const northSeason: Season =
    m >= 2 && m <= 4 ? "spring" :
    m >= 5 && m <= 7 ? "summer" :
    m >= 8 && m <= 10 ? "fall" :
    "winter";
  if (lat >= 0) return northSeason;
  // Southern hemisphere: seasons are flipped.
  const flip: Record<Season, Season> = {
    spring: "fall", summer: "winter", fall: "spring", winter: "summer",
  };
  return flip[northSeason];
}

/** Per-season nudge to the nature signals. Gentle (±~25%) so it tips close
 *  calls without overriding a genuinely-nicer route. `water` scales waterside/
 *  coastline credit; `greenery` scales parks/tree credit (a proxy for both
 *  summer canopy and autumn foliage). */
export interface NatureMultipliers {
  water: number;
  greenery: number;
}

const SEASONAL_NATURE: Record<Season, NatureMultipliers> = {
  // Blossoms + rising green; water pleasant but not yet peak.
  spring: { water: 1.15, greenery: 1.25 },
  // Peak waterfront + full canopy — the Seine quais, lakeshores, shade.
  summer: { water: 1.30, greenery: 1.20 },
  // Foliage season — greenery (tree-lined) is the whole point; water cools off.
  fall: { water: 1.00, greenery: 1.20 },
  // Bare trees, cold/grey water — lean back toward the efficient line.
  winter: { water: 0.85, greenery: 0.85 },
};

/** Nature multipliers for a season; identity (1.0/1.0) when season is absent. */
export function natureMultipliers(season?: Season): NatureMultipliers {
  return season ? SEASONAL_NATURE[season] : { water: 1, greenery: 1 };
}

/** A one-clause seasonal reason hook for the card blurb, or "" when neutral. */
export function seasonBlurb(season: Season): string {
  switch (season) {
    case "spring": return "lovely in spring, with the greenery coming in";
    case "summer": return "at its best in summer — waterfront and shade";
    case "fall": return "gorgeous in autumn, when the foliage turns";
    case "winter": return "";
  }
}

// ---- Seasonal closures ----------------------------------------------------
// Scenic roads that physically close for the season (snow gates). Months are
// 1-12 INCLUSIVE and the set is the CLOSED months. Match is on the road's
// display name (case-insensitive substring). This is the safety piece: when a
// road is closed today, drop it from curated injection so we never route onto a
// gated road. Curated/community roads that don't match are unaffected.
export interface SeasonalClosure {
  match: RegExp;
  /** Months (1-12) during which the road is CLOSED. */
  closedMonths: number[];
  note: string;
}

export const SEASONAL_CLOSURES: SeasonalClosure[] = [
  { match: /going-to-the-sun/i, closedMonths: [10, 11, 12, 1, 2, 3, 4, 5, 6], note: "Glacier NP — snow-closed Oct–Jun" },
  { match: /tioga (pass|road)|ca-?120/i, closedMonths: [11, 12, 1, 2, 3, 4, 5], note: "Yosemite high country — closed Nov–May" },
  { match: /trail ridge/i, closedMonths: [10, 11, 12, 1, 2, 3, 4, 5], note: "Rocky Mtn NP — closed Oct–May" },
  { match: /beartooth/i, closedMonths: [10, 11, 12, 1, 2, 3, 4, 5], note: "Beartooth Hwy — closed mid-Oct–late May" },
  { match: /mount washington auto/i, closedMonths: [11, 12, 1, 2, 3, 4], note: "Mt Washington — closed late Oct–early May" },
];

/** True when a road with this display name is seasonally closed on `date`. */
export function isSeasonallyClosedRoad(name: string | undefined, date: Date): boolean {
  if (!name) return false;
  const month = date.getMonth() + 1; // 1-12
  for (const c of SEASONAL_CLOSURES) {
    if (c.match.test(name) && c.closedMonths.includes(month)) return true;
  }
  return false;
}
